import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { User, Settings, ArrowRight, X, Heart, Brain, Moon, MapPin, BookOpen, Users, CheckCircle, Dumbbell, Coins, Crown } from 'lucide-react';
import { categoryQuestions, CategoryQuestions as CategoryQuestionsType } from '@/components/questionnaire';
import { QuestionnaireData } from '@/types/category';
import { useAuth } from '@/contexts/AuthContext';
import { useProfileManagement } from '@/hooks/useProfileManagement';
import { CreditCalculator } from '@/components/practitioner/CreditCalculator';
import { usePricingRates } from '@/hooks/usePricingRates';
import { usePractitionerCredits } from '@/hooks/usePractitionerCredits';
import { Badge } from '@/components/ui/badge';
import AdditionalQuestions from '@/components/questionnaire/AdditionalQuestions';
import { MultiStepQuestionnaire } from '@/components/questionnaire/MultiStepQuestionnaire';

interface CategorySpecificPersonalizationQuestionnaireProps {
  categoryName: string;
  categoryId: string;
  onSubmit: (data: QuestionnaireData) => void;
  onCancel: () => void;
  isGenerating?: boolean;
  sessionType: 'individual' | 'group';
  isGroupSession?: boolean;
  groupName?: string;
  isPractitionerMode?: boolean; // New prop to indicate practitioner mode
  clientName?: string; // Client name for practitioner mode
  clientData?: any; // Client data for practitioner-created sessions
  initialData?: Record<string, any> | null; // Pre-filled preferences from previous sessions
}

const getCategoryIcon = (categoryId: string) => {
  const icons: Record<string, any> = {
    meditation: Brain,
    anxiety: Heart,
    sleep: Moon,
    massage: Settings,
    motivation: ArrowRight,
    memory: Brain,
    be_still: Heart,
    self_love: Heart,
    fitness: Dumbbell,
    'zen-af': Heart,
    'news-meditation': Settings,
    'yoga-meditation': Settings,
    'addiction-recovery-men': Heart,
    'couples-connection': Users,
    'relationship-decisions': MapPin,
    'sexual-wellness-women': Heart
  };
  return icons[categoryId] || Settings;
};

const getCategoryGradient = (categoryId: string) => {
  const gradients: Record<string, string> = {
    meditation: 'from-slate-50 to-blue-50/30',
    anxiety: 'from-emerald-50 to-green-50/30',
    sleep: 'from-indigo-50 to-violet-50/30',
    massage: 'from-rose-50 to-pink-50/30',
    motivation: 'from-amber-50 to-orange-50/30',
    memory: 'from-sky-50 to-cyan-50/30',
    be_still: 'from-teal-50 to-emerald-50/30',
    self_love: 'from-pink-50 to-rose-50/30',
    fitness: 'from-orange-50 to-red-50/30',
    'zen-af': 'from-orange-50 to-red-50/30',
    'news-meditation': 'from-neutral-50 to-slate-50/30',
    'yoga-meditation': 'from-white to-amber-50/40',
    'addiction-recovery-men': 'from-emerald-50 to-teal-50/30',
    'couples-connection': 'from-purple-50 to-indigo-50/30'
  };
  return gradients[categoryId] || 'from-background to-secondary/10';
};

export const CategorySpecificPersonalizationQuestionnaire: React.FC<CategorySpecificPersonalizationQuestionnaireProps> = ({
  categoryName,
  categoryId,
  onSubmit,
  onCancel,
  isGenerating = false,
  sessionType,
  isGroupSession = false,
  groupName = '',
  isPractitionerMode = false,
  clientName = '',
  clientData,
  initialData = null
}) => {
  const { user } = useAuth();
  const { profile, loading: profileLoading } = useProfileManagement();
  const { rates, audioRate } = usePricingRates();
  const { credits } = usePractitionerCredits();

  console.log("🔥 CategorySpecificPersonalizationQuestionnaire props:", {
    categoryName,
    categoryId,
    sessionType,
    isGroupSession,
    groupName,
    isPractitionerMode,
    clientName
  });

  const categoryConfig = categoryQuestions[categoryId] as CategoryQuestionsType;

  // Determine which questions and title to use based on session type and practitioner mode
  const isGroupSessionType = sessionType === 'group' || isGroupSession;
  let questionsToUse, titleToUse;

  if (isPractitionerMode && isGroupSessionType) {
    // CRITICAL FIX: Use practitionerGroupQuestions for practitioner creating group sessions
    questionsToUse = categoryConfig?.practitionerGroupQuestions || categoryConfig?.groupQuestions || categoryConfig?.practitionerQuestions || categoryConfig?.questions || {};
    titleToUse = categoryConfig?.practitionerGroupTitle || categoryConfig?.groupTitle || `Personalize ${categoryName} for Your Clients`;
  } else if (isPractitionerMode) {
    // Use practitioner questions for practitioners (individual sessions)
    questionsToUse = categoryConfig?.practitionerQuestions || categoryConfig?.questions || {};
    titleToUse = categoryConfig?.practitionerTitle || `Personalize ${categoryName} for Your Client`;
  } else if (isGroupSessionType && categoryConfig?.groupQuestions) {
    // Use group questions for regular users
    questionsToUse = categoryConfig.groupQuestions;
    titleToUse = categoryConfig.groupTitle || `Personalize Your Group ${categoryName}`;
  } else {
    // Use regular questions for individual sessions
    questionsToUse = categoryConfig?.questions || {};
    titleToUse = categoryConfig?.title || `Personalize Your ${categoryName}`;
  }

  console.log("🔥 Questions selection DEBUG:", {
    isGroupSessionType,
    isPractitionerMode,
    hasGroupQuestions: !!categoryConfig?.groupQuestions,
    hasPractitionerQuestions: !!categoryConfig?.practitionerQuestions,
    hasPractitionerGroupQuestions: !!categoryConfig?.practitionerGroupQuestions,
    questionsCount: Object.keys(questionsToUse).length,
    titleToUse,
    questionKeys: Object.keys(questionsToUse),
    // Show which question set was actually used
    selectedQuestionSet: isPractitionerMode && isGroupSessionType ? 'practitionerGroupQuestions' :
                         isPractitionerMode ? 'practitionerQuestions' :
                         isGroupSessionType ? 'groupQuestions' : 'questions',
    // Show the actual label for groupFrustration if it exists
    groupFrustrationLabel: questionsToUse.groupFrustration?.label || 'N/A'
  });
  
  // Check if user has a biography
  const hasBiography = !!(profile?.biography && profile.biography.trim().length > 0);

  // Initialize formData with defaults or pre-filled values from previous sessions
  const [formData, setFormData] = useState<QuestionnaireData>(() => {
    // Default values
    const defaults = {
      // Only session-specific fields - biography comes from profile
      firstName: user?.user_metadata?.full_name?.split(' ')[0] || profile?.first_name || '',
      lastName: user?.user_metadata?.full_name?.split(' ').slice(1).join(' ') || profile?.last_name || '',
      age: '',
      occupation: '',
      relationship: '',
      location: '',
      hobbies: '',
      stressors: '',
      goals: '',
      // Session-specific preferences
      preferredStyle: '',
      voiceType: '',
      voiceGender: 'female',
      specific_voice_id: 'female_default', // Default voice ID matching voiceGender
      primaryGoal: '',
      triggers: '',
      desiredFeeling: '',
      sessionLength: '5 minutes',
      backgroundSounds: true,
      backgroundMusicSelection: 'universal_background.mp3',
      additionalNotes: '',
      additionalInfo: '',
      environment: '',
      useEnvironment: true,
      specificFocus: '',
      specialOccasions: '',
      explicitLanguageLevel: 8,  // Default for zen-af and news-meditation
      humorLevel: 'Moderate',
      name: user?.user_metadata?.full_name || user?.email || '',
      usePersonalBiography: sessionType === 'individual'
    };

    // If initialData is provided, merge it with defaults
    if (initialData) {
      console.log('📋 Pre-filling questionnaire with stored preferences:', initialData);
      return {
        ...defaults,
        ...initialData,
        // Don't override these session-specific fields
        additionalNotes: '', // Always start with empty notes
        triggers: '', // This varies by session
        primaryGoal: initialData.primaryGoal || '', // Use stored but allow empty
      };
    }

    return defaults;
  });


  // Update form data when profile loads - with proper dependencies to prevent infinite loops
  // IMPORTANT: In practitioner mode, don't overwrite with practitioner's profile
  // The form should show CLIENT data from initialData, not practitioner data
  useEffect(() => {
    // Skip this effect in practitioner mode - client data comes from initialData
    if (isPractitionerMode) {
      return;
    }

    if (profile && profile.first_name !== undefined) {
      setFormData(prev => {
        // Only update if values actually changed to prevent loops
        const needsUpdate =
          (profile.first_name && profile.first_name !== prev.firstName) ||
          (profile.last_name && profile.last_name !== prev.lastName) ||
          (profile.full_name && profile.full_name !== prev.name);

        if (needsUpdate) {
          console.log('📝 Updating form data with profile:', {
            firstName: profile.first_name,
            lastName: profile.last_name,
            fullName: profile.full_name
          });

          return {
            ...prev,
            firstName: profile.first_name || prev.firstName,
            lastName: profile.last_name || prev.lastName,
            name: profile.full_name || prev.name,
          };
        }

        return prev;
      });
    }
  }, [profile?.first_name, profile?.last_name, profile?.full_name, isPractitionerMode]); // Only depend on specific profile fields

  // No longer need this separate state since it's determined by sessionType
  // const [usePersonalBiography, setUsePersonalBiography] = useState(true);

  const Icon = getCategoryIcon(categoryId);
  const gradient = getCategoryGradient(categoryId);

  const handleSubmit = () => {
    console.log('🔥 handleSubmit called in questionnaire');
    console.log('🔥 Form validation check:', isFormValid());
    console.log('🔥 isGenerating state:', isGenerating);
    
    if (!isFormValid()) {
      console.log('🚨 Form validation failed, cannot submit');
      return;
    }
    
    if (isGenerating) {
      console.log('🚨 Already generating, preventing duplicate submission');
      return;
    }
    
    // Ensure we have the minimum required fields
    // CRITICAL: Biography integration must include actual biography content for personalization
    const completeData: QuestionnaireData = {
      ...formData,
      usePersonalBiography: sessionType === 'individual' && hasBiography, // Use existing biography if available
      // Use client biography for practitioner mode, else use practitioner's own profile
      biography: sessionType === 'individual' && hasBiography
        ? (isPractitionerMode && clientData?.biography ? clientData.biography : profile?.biography)
        : undefined, // REQUIRED: Include actual biography content for AI personalization
      // Map additional category-specific fields if they exist
      ...(questionsToUse.currentNews && { currentNews: formData.currentNews }),
      ...(questionsToUse.newsConsumption && { newsConsumption: formData.newsConsumption }),
      ...(questionsToUse.explicitLanguageLevel && { explicitLanguageLevel: formData.explicitLanguageLevel }),
      ...(questionsToUse.humorLevel && { humorLevel: formData.humorLevel }),
      ...(questionsToUse.specialOccasions && { specialOccasions: formData.specialOccasions })
    };
    
    console.log('📋 Submitting questionnaire data:', {
      ...completeData,
      hasBiography,
      biographyLength: profile?.biography?.length || 0
    });
    
    console.log('🔥 Calling onSubmit with data');
    try {
      onSubmit(completeData);
      console.log('🔥 onSubmit called successfully');
    } catch (error) {
      console.error('🚨 Error calling onSubmit:', error);
    }
  };

  const handleToggleEnvironment = (enabled: boolean) => {
    setFormData(prev => ({ 
      ...prev, 
      useEnvironment: enabled,
      environment: enabled ? prev.environment : '' // Clear environment if disabled
    }));
  };

  const handleEnvironmentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const text = e.target.value;
    const maxChars = 500;
    
    console.log('Environment textarea change:', text);
    
    // Allow typing, just truncate if over limit
    const finalText = text.length > maxChars ? text.slice(0, maxChars) : text;
    setFormData(prev => ({ ...prev, environment: finalText }));
  };

  const renderEnvironmentQuestion = () => {
    const currentCharCount = formData.environment ? formData.environment.length : 0;
    const maxChars = 500;

    return (
      <Card className="border border-border/30 bg-gradient-to-r from-white to-amber-50/20 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <MapPin className="h-5 w-5 text-amber-600" />
              </div>
              <CardTitle className="text-lg font-light text-foreground">Environment Personalization</CardTitle>
            </div>
            <div className="flex items-center gap-2">
              <Settings className="h-4 w-4 text-muted-foreground" />
              <Switch
                checked={formData.useEnvironment ?? true}
                onCheckedChange={handleToggleEnvironment}
                className="data-[state=checked]:bg-amber-500"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {formData.useEnvironment ? (
              "Tell us about your current surroundings to create a more immersive, personalized meditation experience."
            ) : (
              "Environment personalization is disabled. Your meditation will use general language without specific location references."
            )}
          </div>
          
          {formData.useEnvironment && (
            <div className="space-y-2">
              <Label htmlFor="environment" className="text-sm font-medium">
                Describe your current environment *
              </Label>
              <Textarea
                id="environment"
                placeholder="e.g., sitting in my cozy bedroom with soft lighting, lying on a yoga mat in the living room, at my desk in a quiet office..."
                value={formData.environment || ''}
                onChange={handleEnvironmentChange}
                rows={4}
                className="resize-none rounded-xl border-input bg-background"
                required={formData.useEnvironment}
              />
              <div className="flex justify-between items-center text-sm">
                <span className={currentCharCount > maxChars ? "text-red-500" : "text-muted-foreground"}>
                  {currentCharCount}/{maxChars} characters
                </span>
                {formData.useEnvironment && currentCharCount === 0 && (
                  <span className="text-red-500">* Required when enabled</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };


  const renderBiographySection = () => {
    // Early return: If user has biography for individual sessions, show simple confirmation only
    if (hasBiography && sessionType === 'individual') {
      return (
        <Card className="border border-emerald-200 bg-gradient-to-r from-white to-emerald-50/30 shadow-sm">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-light text-emerald-800">Biography Ready</CardTitle>
                <div className="text-sm text-emerald-700 mt-1">
                  We'll use your existing biography to create a personalized meditation experience.
                  <span className="block mt-2 text-xs text-emerald-600 font-medium">
                    Biography length: {profile?.biography?.length || 0} characters
                  </span>
                </div>
              </div>
            </div>
          </CardHeader>
        </Card>
      );
    }

    // Only reach here if user doesn't have biography OR is group session
    // Show full biography collection form
    return (
      <Card className="border border-border/30 bg-gradient-to-r from-white to-sky-50/20 shadow-sm">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-3">
              <div className="p-2 bg-sky-100 rounded-lg">
                <BookOpen className="h-5 w-5 text-sky-600" />
              </div>
              <div>
                <CardTitle className="text-lg font-light text-foreground">
                  {sessionType === 'individual' ? 'Personal Information' : 'Session Information'}
                </CardTitle>
                <div className="text-sm text-muted-foreground mt-1">
                  {sessionType === 'individual' 
                    ? 'This information helps us create deeply personalized experiences for you. Your first name is required.'
                    : 'Basic information for this session. Your first name is required.'
                  }
                </div>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="firstName" className="text-sm font-medium">
                  First Name *
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, firstName: e.target.value }))}
                  placeholder="e.g., John"
                  className="rounded-xl border-border/50 bg-background"
                  required
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="lastName" className="text-sm font-medium">
                  Last Name
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, lastName: e.target.value }))}
                  placeholder="e.g., Smith"
                  className="rounded-xl border-border/50 bg-background"
                />
              </div>
            </div>

            {sessionType === 'individual' && !hasBiography && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="age" className="text-sm font-medium">
                      Age
                    </Label>
                    <Input
                      id="age"
                      value={formData.age || ''}
                      onChange={(e) => setFormData(prev => ({ ...prev, age: e.target.value }))}
                      placeholder="e.g., 30"
                      type="number"
                      className="rounded-xl border-border/50 bg-background"
                    />
                  </div>
                  <div>
                    <Label htmlFor="relationship" className="text-sm font-medium">
                      Relationship Status
                    </Label>
                    <Select value={formData.relationship || ''} onValueChange={(value) => setFormData(prev => ({ ...prev, relationship: value }))}>
                      <SelectTrigger className="rounded-xl border-border/50 bg-background w-full">
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent className="z-[200] bg-background border border-border shadow-lg max-h-60 overflow-y-auto">
                        <SelectItem value="single" className="cursor-pointer hover:bg-accent focus:bg-accent">Single</SelectItem>
                        <SelectItem value="in a relationship" className="cursor-pointer hover:bg-accent focus:bg-accent">In a relationship</SelectItem>
                        <SelectItem value="married" className="cursor-pointer hover:bg-accent focus:bg-accent">Married</SelectItem>
                        <SelectItem value="divorced" className="cursor-pointer hover:bg-accent focus:bg-accent">Divorced</SelectItem>
                        <SelectItem value="widowed" className="cursor-pointer hover:bg-accent focus:bg-accent">Widowed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label htmlFor="occupation" className="text-sm font-medium">
                    Occupation
                  </Label>
                  <Input
                    id="occupation"
                    value={formData.occupation || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, occupation: e.target.value }))}
                    placeholder="e.g., Software Engineer, Teacher, Student"
                    className="rounded-xl border-border/50 bg-background"
                  />
                </div>

                <div>
                  <Label htmlFor="location" className="text-sm font-medium">
                    Location
                  </Label>
                  <Input
                    id="location"
                    value={formData.location || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                    placeholder="e.g., New York City, California"
                    className="rounded-xl border-border/50 bg-background"
                  />
                </div>

                <div>
                  <Label htmlFor="hobbies" className="text-sm font-medium">
                    Hobbies & Interests
                  </Label>
                  <Textarea
                    id="hobbies"
                    value={formData.hobbies || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, hobbies: e.target.value }))}
                    placeholder="e.g., reading, hiking, cooking, playing guitar"
                    className="rounded-xl border-border/50 bg-background min-h-[60px] resize-none"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="stressors" className="text-sm font-medium">
                    Main Stressors
                  </Label>
                  <Textarea
                    id="stressors"
                    value={formData.stressors || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, stressors: e.target.value }))}
                    placeholder="e.g., work deadlines, family responsibilities, financial concerns"
                    className="rounded-xl border-border/50 bg-background min-h-[60px] resize-none"
                    rows={2}
                  />
                </div>

                <div>
                  <Label htmlFor="goals" className="text-sm font-medium">
                    Current Goals
                  </Label>
                  <Textarea
                    id="goals"
                    value={formData.goals || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, goals: e.target.value }))}
                    placeholder="e.g., improve work-life balance, learn new skills, get healthier"
                    className="rounded-xl border-border/50 bg-background min-h-[60px] resize-none"
                    rows={2}
                  />
                </div>
              </>
            )}
          </CardContent>
        </Card>
    );
  };

  const isFormValid = () => {
    // Don't validate if still loading profile
    if (profileLoading) return false;

    // First name is mandatory only for individual sessions
    if (sessionType === 'individual' && (!formData.firstName || formData.firstName.trim().length === 0)) {
      return false;
    }
    
    // Voice selection validation (common to all categories) - specific_voice_id is optional
    const hasVoiceSelection = formData.voiceGender;
    
    // If environment is enabled, it must be filled
    if (formData.useEnvironment && (!formData.environment || formData.environment.trim().length === 0)) {
      return false;
    }
    
    // Category-specific validation
    let categoryValidation = false;
    
    if (categoryId === 'zen-af') {
      // Zen AF validation - requires specificFocus and deeperReason
      if (isGroupSessionType) {
        categoryValidation = !!(
          formData.groupFrustration && 
          formData.groupFrustration.trim() && 
          formData.deeperReason && 
          formData.deeperReason.trim() &&
          formData.sessionLength
        );
      } else {
        // Individual Zen AF validation - check for Zen AF specific fields
        categoryValidation = !!(
          formData.specificFocus && 
          formData.specificFocus.trim() && 
          formData.deeperReason && 
          formData.deeperReason.trim() &&
          formData.sessionLength
        );
      }
    } else if (categoryId === 'news-meditation') {
      // News meditation validation
      if (isGroupSessionType) {
        categoryValidation = !!(
          formData.groupFrustration && 
          formData.groupFrustration.trim() && 
          formData.triggers && 
          formData.triggers.trim() &&
          formData.sessionLength
        );
      } else {
        // Individual news meditation validation - check for required news fields
        categoryValidation = !!(
          formData.currentNews && 
          formData.currentNews.trim() && 
          formData.primaryGoal && 
          formData.voiceType && 
          formData.sessionLength
        );
      }
    } else if (categoryId === 'yoga-meditation') {
      if (isGroupSessionType) {
        // Group yoga validation - check for group-specific field names
        categoryValidation = !!(
          formData.groupExperienceLevel &&
          formData.groupYogaStyle &&
          formData.groupBeginningPosition &&
          formData.groupFocusAreas &&
          formData.sessionTiming &&
          formData.instructorDirectives &&
          formData.groupSpecialSubjects
        );
      } else {
        // Individual yoga validation
        categoryValidation = !!(
          formData.experienceLevel &&
          formData.yogaStyle &&
          formData.beginningPosition &&
          formData.endingPosition &&
          formData.focusAreas &&
          formData.breathWork &&
          formData.sessionIntention
        );
      }
    } else if (categoryId === 'addiction-recovery-men') {
      // Addiction Recovery validation
      if (isGroupSessionType) {
        categoryValidation = !!(
          formData.groupType &&
          formData.groupRecoveryStage &&
          formData.groupFocus &&
          formData.sessionLength
        );
      } else {
        categoryValidation = !!(
          formData.recoveryStage &&
          formData.primaryFocus &&
          formData.emotionalNeeds &&
          formData.sessionLength
        );
      }
    } else if (categoryId === 'couples-connection') {
      // Couples Connection validation (uses standard fields since it's a therapeutic category)
      if (isGroupSessionType) {
        categoryValidation = !!(
          formData.primaryGoal &&
          formData.sessionLength
        );
      } else {
        categoryValidation = !!(
          formData.preferredStyle && 
          formData.primaryGoal && 
          formData.sessionLength
        );
      }
    } else if (categoryId === 'relationship-decisions') {
      // Relationship Decisions validation
      if (isGroupSessionType) {
        categoryValidation = !!(
          formData.groupType &&
          formData.groupFocus &&
          formData.experienceLevels &&
          formData.sessionLength
        );
      } else {
        categoryValidation = !!(
          formData.decisionStage &&
          formData.primaryConcern &&
          formData.valuesExploration &&
          formData.emotionalState &&
          formData.supportNeeded &&
          formData.sessionApproach &&
          formData.sessionLength
        );
      }
    } else if (categoryId === 'sexual-wellness-women') {
      // Sexual Wellness Women validation
      if (isGroupSessionType) {
        categoryValidation = !!(
          formData.groupContext &&
          formData.groupFocus &&
          formData.groupDynamics &&
          formData.safetyLevel &&
          formData.voiceType &&
          formData.sessionLength
        );
      } else {
        categoryValidation = !!(
          formData.primaryFocus &&
          formData.healingApproach &&
          formData.voiceType &&
          formData.emotionalTone &&
          formData.sessionLength
        );
      }
    } else if (categoryId === 'life_celebrations') {
      // Life Celebrations validation
      if (isGroupSessionType) {
        categoryValidation = !!(
          formData.celebrationType &&
          formData.celebrationFocus &&
          formData.energyLevel &&
          formData.sessionLength
        );
      } else {
        // Individual celebration validation
        categoryValidation = !!(
          formData.celebrationType &&
          formData.celebrationFocus &&
          formData.energyLevel &&
          formData.sessionLength
        );
      }
    } else {
      // Check for the most common required fields across categories
      // For group questions, we need to adapt validation to the specific group question structure
      if (isGroupSessionType) {
        // Most group questions have these core fields
        categoryValidation = !!(
          (questionsToUse.groupSize || questionsToUse.groupType || questionsToUse.sessionLength) &&
          (questionsToUse.voiceType || questionsToUse.collectiveGoal) &&
          formData.sessionLength
        );
      } else {
        // Standard category validation (meditation, sleep, anxiety, etc.)
        categoryValidation = !!(
          formData.preferredStyle && 
          formData.voiceType && 
          formData.primaryGoal && 
          formData.sessionLength
        );
      }
    }
    
    console.log('🔥 Form validation debug:', {
      categoryId,
      isGroupSessionType,
      hasVoiceSelection,
      categoryValidation,
      firstName: formData.firstName,
      voiceGender: formData.voiceGender,
      specific_voice_id: formData.specific_voice_id,
      environment: formData.environment,
      useEnvironment: formData.useEnvironment,
      // Zen AF fields for debugging
      specificFocus: formData.specificFocus,
      deeperReason: formData.deeperReason,
      sessionLength: formData.sessionLength,
      // Group yoga fields for debugging
      groupExperienceLevel: formData.groupExperienceLevel,
      groupYogaStyle: formData.groupYogaStyle,
      groupBeginningPosition: formData.groupBeginningPosition,
      sessionTiming: formData.sessionTiming,
      groupFocusAreas: formData.groupFocusAreas,
      instructorDirectives: formData.instructorDirectives,
      groupYogaFocus: formData.groupSpecialSubjects
    });
    
    return hasVoiceSelection && categoryValidation;
  };

  const updateFormData = (field: keyof QuestionnaireData, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const renderQuestionField = (questionKey: string, question: any) => {
    const { label, type, options, placeholder, min, max, defaultValue } = question;

    switch (type) {
      case 'radio':
        return (
          <div className="space-y-3">
            <Label className="text-base font-medium">{label}</Label>
            <RadioGroup
              value={formData[questionKey as keyof QuestionnaireData] as string}
              onValueChange={(value) => updateFormData(questionKey as keyof QuestionnaireData, value)}
              className="grid grid-cols-1 gap-3"
            >
              {options?.map((option: string) => (
                <div key={option} className="flex items-center space-x-2 border border-border/50 rounded-xl p-3 hover:bg-accent/10 transition-colors">
                  <RadioGroupItem value={option} id={option} />
                  <Label htmlFor={option} className="cursor-pointer font-light text-sm flex-1">
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>
        );

      case 'select':
        return (
          <div className="space-y-3">
            <Label className="text-base font-medium">{label}</Label>
            <Select
              value={formData[questionKey as keyof QuestionnaireData] as string}
              onValueChange={(value) => updateFormData(questionKey as keyof QuestionnaireData, value)}
            >
              <SelectTrigger className="rounded-xl border-border/50 bg-background w-full">
                <SelectValue placeholder={`Choose ${label.toLowerCase()}`} />
              </SelectTrigger>
              <SelectContent className="z-[200] bg-background border border-border shadow-lg max-h-60 overflow-y-auto">
                {options?.map((option: string) => (
                  <SelectItem 
                    key={option} 
                    value={option}
                    className="cursor-pointer hover:bg-accent focus:bg-accent"
                  >
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );

      case 'textarea':
        return (
          <div className="space-y-3">
            <Label className="text-base font-medium">{label}</Label>
            <Textarea
              value={formData[questionKey as keyof QuestionnaireData] as string || ''}
              onChange={(e) => updateFormData(questionKey as keyof QuestionnaireData, e.target.value)}
              placeholder={placeholder}
              className="min-h-[100px] rounded-xl border-border/50 bg-background"
            />
            <div className="text-xs text-muted-foreground">
              {((formData[questionKey as keyof QuestionnaireData] as string) || '').length}/{questionKey === 'currentNews' ? '2000' : '500'} characters
            </div>
          </div>
        );

      case 'text':
        return (
          <div className="space-y-3">
            <Label className="text-base font-medium">{label}</Label>
            <Input
              value={formData[questionKey as keyof QuestionnaireData] as string || ''}
              onChange={(e) => updateFormData(questionKey as keyof QuestionnaireData, e.target.value)}
              placeholder={placeholder}
              className="rounded-xl border-border/50 bg-background"
            />
          </div>
        );

      case 'slider':
        return (
          <div className="space-y-3">
            <Label className="text-base font-medium">{label}</Label>
            <div className="space-y-4">
              <Slider
                value={[formData[questionKey as keyof QuestionnaireData] as number || defaultValue || 1]}
                onValueChange={([value]) => updateFormData(questionKey as keyof QuestionnaireData, value)}
                min={min || 1}
                max={max || 10}
                step={1}
                className="w-full"
              />
              <div className="text-center">
                <span className="text-lg font-medium text-primary">
                  {formData[questionKey as keyof QuestionnaireData] || defaultValue || 1}
                </span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  if (!categoryConfig) {
    console.log("🔥 ERROR: No categoryConfig found for categoryId:", categoryId);
    console.log("🔥 Available categories:", Object.keys(categoryQuestions));
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[100] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-6 text-center">
            <p>Category questions not found for {categoryName}</p>
            <p className="text-sm text-muted-foreground mt-2">CategoryId: {categoryId}</p>
            <p className="text-sm text-muted-foreground">Available: {Object.keys(categoryQuestions).join(', ')}</p>
            <Button onClick={onCancel} className="mt-4">Back</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div
      data-overlay="questionnaire"
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 bg-black/20 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={onCancel}
    >
      <div className="absolute top-4 right-4 z-[101]">
        <Button variant="secondary" size="sm" onClick={onCancel} aria-label="Close personalization overlay">
          Close
        </Button>
      </div>
      <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
        <MultiStepQuestionnaire
          categoryId={categoryId}
          categoryName={categoryName}
          categoryConfig={categoryConfig}
          hasBiography={hasBiography}
          sessionType={sessionType}
          groupSession={isGroupSession ? { id: groupName, name: groupName, group_name: groupName } : undefined}
          isPractitionerMode={isPractitionerMode}
          clientData={clientData}
          initialData={initialData}
          isNameDisabled={!!profile?.first_name}
          isGenerating={isGenerating}
          onSubmit={(data) => {
            // Merge profile data into submission
            const finalData: QuestionnaireData = {
              ...data,
              firstName: data.firstName || profile?.first_name || '',
              lastName: data.lastName || profile?.last_name || '',
              biography: profile?.biography || '',
            };
            onSubmit(finalData);
          }}
          onCancel={onCancel}
        />
      </div>
    </div>
  );
};

export default CategorySpecificPersonalizationQuestionnaire;